import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSiteSearch } from "@/hooks/useSiteSearch";

export default function HeroSection() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const results = useSiteSearch(query);

  const go = (to: string) => {
    setQuery("");
    setFocused(false);
    navigate(to);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (results.length > 0) go(results[0].to);
  };

  return (
    <section className="relative w-full h-[520px] md:h-[600px] overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=dramatic%20basketball%20arena%20interior%20with%20glowing%20orange%20spotlights%20and%20deep%20navy%20surroundings%2C%20cinematic%20wide%20angle%2C%20blurred%20crowd%2C%20professional%20sports%20broadcast%20atmosphere%20with%20strong%20contrast&width=1920&height=1080&seq=bb-home-hero-01&orientation=landscape"
        alt="篮球比赛场馆全景"
        title="高清篮球比赛直播 - 篮球直播"
        className="absolute inset-0 w-full h-full object-top object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-primary-950/85 via-primary-950/70 to-primary-950/90" />

      <div className="relative h-full w-full px-4 md:px-6 flex flex-col items-center justify-center text-center">
        <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-accent-500/95 text-background-50 dark:text-foreground-950 text-xs font-label font-semibold tracking-wide mb-5 bb-fade-up">
          <span className="bb-live-dot w-1.5 h-1.5 rounded-full bg-background-50 dark:bg-foreground-950" />
          今晚 2 场赛事正在直播
        </span>

        <h1 className="font-heading font-semibold text-background-50 text-3xl md:text-5xl lg:text-6xl leading-tight bb-fade-up">
          篮球直播
        </h1>
        <p className="mt-4 max-w-2xl text-primary-100 text-sm md:text-base leading-relaxed bb-fade-up">
          高清篮球比赛实况与赛程直播平台，覆盖国内外各大篮球联赛，实时观看比赛、查阅赛程、浏览赛事资讯。
        </p>

        <form
          onSubmit={submit}
          className="relative w-full max-w-xl mt-8 bb-fade-up"
        >
          <div className="flex items-center bg-background-50 rounded-full pl-4 pr-1.5 py-1.5 gap-2">
            <i className="ri-search-line text-foreground-500 text-lg" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => window.setTimeout(() => setFocused(false), 180)}
              placeholder="搜索赛事、球队、球员、资讯关键词"
              className="flex-1 bg-transparent text-sm md:text-base text-foreground-900 placeholder:text-foreground-400 focus:outline-none py-1.5 min-w-0"
            />
            <button
              type="submit"
              className="shrink-0 px-4 md:px-6 py-2.5 rounded-full bg-accent-500 hover:bg-accent-600 text-background-50 dark:text-foreground-950 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </div>

          {focused && query.trim() !== "" && (
            <div className="absolute left-0 right-0 top-full mt-2 bg-background-50 rounded-lg border border-background-200 py-1.5 max-h-80 overflow-y-auto text-left shadow-sm">
              {results.length === 0 ? (
                <div className="px-4 py-6 text-center">
                  <i className="ri-search-eye-line text-2xl text-foreground-400" />
                  <p className="mt-2 text-sm text-foreground-600">
                    未找到与“{query}”相关的内容，试试球队名或联赛名称
                  </p>
                </div>
              ) : (
                results.map((r) => (
                  <button
                    key={`${r.type}-${r.key}`}
                    type="button"
                    onMouseDown={() => go(r.to)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs shrink-0">
                      <i
                        className={
                          r.type === "比赛"
                            ? "ri-basketball-line"
                            : r.type === "球队"
                            ? "ri-shield-line"
                            : "ri-newspaper-line"
                        }
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-foreground-900 truncate">{r.label}</span>
                      <span className="block text-xs text-foreground-500">{r.sub}</span>
                    </span>
                    <span className="text-[10px] font-label text-secondary-700 bg-secondary-100 px-1.5 py-0.5 rounded shrink-0">
                      {r.type}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </form>

        <div className="flex flex-wrap items-center justify-center gap-3 mt-7">
          <button
            type="button"
            onClick={() => navigate("/match/m-2001")}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-live-line text-lg" />
            进入直播
          </button>
          <button
            type="button"
            onClick={() => navigate("/schedule")}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 border border-background-50/30 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-calendar-line text-lg" />
            查看完整赛程
          </button>
        </div>
      </div>
    </section>
  );
}